/**
 * @file OrderItemRow.tsx
 * @description Renders individual ticket order item with quantities, modifiers, and preparation notes.
 */

import React from 'react';
import type { KitchenOrderItem } from '../../../types/order.types';

interface OrderItemRowProps {
  item: KitchenOrderItem;
}

export const OrderItemRow: React.FC<OrderItemRowProps> = ({ item }) => {
  const isCancelled = item.status === 'cancelled';

  return (
    <div className={`py-2 px-2.5 rounded-lg transition-colors ${
      isCancelled ? 'opacity-40 line-through bg-zinc-900/50' : 'bg-zinc-900/80 hover:bg-zinc-900'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-amber-500/10 text-amber-400 font-mono font-black text-sm shrink-0 border border-amber-500/20">
            {item.quantity}×
          </span>
          <div className="min-w-0">
            <p className="font-bold text-sm text-zinc-100 leading-snug truncate">
              {item.name}
            </p>

            {item.cooking_style && (
              <p className="text-xs text-amber-300 font-medium mt-0.5">
                • {item.cooking_style}
              </p>
            )}

            {item.modifiers && item.modifiers.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {item.modifiers.map((mod, idx) => {
                  const isRemoval = mod.group_type === 'REMOVAL' || 
                    /^(sans|no|without|pas de)\b/i.test(mod.name.trim());
                  const isPreference = mod.group_type === 'COOKING_PREFERENCE';
                  const isSubstitution = mod.group_type === 'SUBSTITUTION';
                  const qtyMultiplier = mod.quantity && mod.quantity > 1 ? `${mod.quantity}× ` : '';

                  let badgeStyles = 'bg-zinc-800 text-zinc-300 border-zinc-700/50';
                  if (isRemoval) {
                    badgeStyles = 'bg-rose-950/60 text-rose-300 border-rose-800/80 font-bold uppercase';
                  } else if (isPreference) {
                    badgeStyles = 'bg-amber-950/60 text-amber-300 border-amber-800/80 font-bold';
                  } else if (isSubstitution) {
                    badgeStyles = 'bg-sky-950/60 text-sky-300 border-sky-800/80 font-semibold';
                  }

                  return (
                    <span
                      key={idx}
                      className={`inline-block text-[11px] font-medium px-1.5 py-0.5 rounded border transition-colors ${badgeStyles}`}
                    >
                      {isRemoval ? '✕ ' : '+ '}{qtyMultiplier}{mod.name}
                    </span>
                  );
                })}
              </div>
            )}

            {item.notes && (
              <p className="text-xs text-rose-300 font-medium bg-rose-950/40 px-2 py-0.5 rounded border border-rose-900/40 mt-1">
                Note: {item.notes}
              </p>
            )}

            {isCancelled && item.cancelled_reason && (
              <p className="text-[11px] text-rose-400 font-semibold mt-1">
                Cancelled: {item.cancelled_reason}
              </p>
            )}
          </div>
        </div>

        {item.kitchen_station?.toLowerCase() === 'drinks' ? (
          <span className="text-[10px] uppercase font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded shrink-0">
            Bar Item
          </span>
        ) : item.course ? (
          <span className="text-[10px] uppercase font-bold text-zinc-500 bg-zinc-800/80 px-1.5 py-0.5 rounded shrink-0">
            {item.course}
          </span>
        ) : null}
      </div>
    </div>
  );
};
